import { useState } from 'react';

import { useSnackbar } from '@/hooks/useSnackbar';
import {
  UpdateReferralStatusRequestCloseReasonEnum,
  UpdateReferralStatusRequestStatusEnum
} from '@/types/crm-screener-api';

import { CloseReferralDialog } from '../ReferralManagement/components/CloseReferralDialog';
import { ConfirmReferralDialog } from '../ReferralManagement/components/ConfirmReferralDialog';
import { ConfirmReferralSimpleDialog } from '../ReferralManagement/components/ConfirmReferralSimpleDialog';
import { EditDischargeDateModal } from '../ReferralManagement/components/EditDischargeDateModal';
import { useUpdateReferralMutation } from '../ReferralManagement/hooks/useUpdateReferralMutation';
import { useUpdateReferralStatusMutation } from '../ReferralManagement/hooks/useUpdateReferralStatusMutation';
import {
  isReferralClosable,
  isReferralConfirmable
} from '../utils/referralStatusUtils';

export { isReferralClosable, isReferralConfirmable };

export interface OutboundReferralAction {
  key: 'close-referral' | 'edit-discharge-date' | 'confirm-referral';
  label: string;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
}

export interface OutboundReferralActionTarget {
  referral_id: string;
  status: string;
  first_name: string | null;
  last_name: string | null;
  target_discharge_date?: string | null;
  recipients: Array<{
    facility_id: string;
    status: string;
    facility_name?: string | null;
  }>;
}

interface UseOutboundReferralActionsOptions {
  referral: OutboundReferralActionTarget;
  confirmVariant?: 'picker' | 'simple';
}

function getPatientName(referral: OutboundReferralActionTarget) {
  return [referral.first_name, referral.last_name].filter(Boolean).join(' ');
}

type CloseReason =
  (typeof UpdateReferralStatusRequestCloseReasonEnum)[keyof typeof UpdateReferralStatusRequestCloseReasonEnum];

export interface UseOutboundReferralActionsResult {
  actions: OutboundReferralAction[];
  dialogElement: React.ReactNode;
}

export function useOutboundReferralActions({
  referral,
  confirmVariant = 'picker'
}: UseOutboundReferralActionsOptions): UseOutboundReferralActionsResult {
  const { showSnackbar } = useSnackbar();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [isEditDateDialogOpen, setIsEditDateDialogOpen] = useState(false);
  const updateStatusMutation = useUpdateReferralStatusMutation(
    referral.referral_id
  );
  const updateReferralMutation = useUpdateReferralMutation(
    referral.referral_id
  );

  const patientName = getPatientName(referral);

  const isConfirmable = isReferralConfirmable(referral.status);
  const isClosable = isReferralClosable(referral.status);

  const acceptedRecipients = referral.recipients.filter(
    (r) => r.status === 'accepted'
  );

  const handleConfirm = (facilityId: string) => {
    updateStatusMutation.mutate(
      {
        status: UpdateReferralStatusRequestStatusEnum.Confirmed,
        facility_id: facilityId
      },
      {
        onSuccess: () => {
          setIsConfirmDialogOpen(false);
        },
        onError: () => {
          showSnackbar({
            message: 'Failed to confirm referral. Please try again.',
            severity: 'error'
          });
        }
      }
    );
  };

  const handleClose = (reason: CloseReason) => {
    updateStatusMutation.mutate(
      {
        status: UpdateReferralStatusRequestStatusEnum.Closed,
        close_reason: reason
      },
      {
        onSuccess: () => {
          setIsCloseDialogOpen(false);
        },
        onError: () => {
          showSnackbar({
            message: 'Failed to close referral. Please try again.',
            severity: 'error'
          });
        }
      }
    );
  };

  const handleEditDischargeDate = (date: Date) => {
    updateReferralMutation.mutate(
      { target_discharge_date: date },
      {
        onSuccess: () => {
          setIsEditDateDialogOpen(false);
          showSnackbar({
            message: 'Discharge date updated successfully',
            severity: 'success'
          });
        },
        onError: () => {
          showSnackbar({
            message: 'Failed to update discharge date. Please try again.',
            severity: 'error'
          });
        }
      }
    );
  };

  const confirmDisabledReason = !isConfirmable
    ? 'Referral is already confirmed or closed'
    : acceptedRecipients.length === 0
      ? 'No accepted recipients to confirm'
      : undefined;

  const closeDisabledReason = !isClosable
    ? 'Referral is already closed'
    : undefined;

  const actions: OutboundReferralAction[] = [
    {
      key: 'confirm-referral',
      label: 'Confirm referral',
      onClick: () => setIsConfirmDialogOpen(true),
      disabled: !!confirmDisabledReason,
      disabledReason: confirmDisabledReason
    },
    {
      key: 'close-referral',
      label: 'Close referral',
      onClick: () => setIsCloseDialogOpen(true),
      disabled: !!closeDisabledReason,
      disabledReason: closeDisabledReason
    },
    {
      key: 'edit-discharge-date',
      label: 'Edit discharge date',
      onClick: () => setIsEditDateDialogOpen(true)
    }
  ];

  const dialogElement = (
    <>
      {confirmVariant === 'simple' ? (
        <ConfirmReferralSimpleDialog
          open={isConfirmDialogOpen}
          onClose={() => setIsConfirmDialogOpen(false)}
          onConfirm={() => {
            const facilityId = acceptedRecipients[0]?.facility_id;
            if (facilityId) {
              handleConfirm(facilityId);
            }
          }}
          isLoading={updateStatusMutation.isLoading}
          facilityName={acceptedRecipients[0]?.facility_name}
        />
      ) : (
        <ConfirmReferralDialog
          open={isConfirmDialogOpen}
          onClose={() => setIsConfirmDialogOpen(false)}
          onConfirm={handleConfirm}
          isLoading={updateStatusMutation.isLoading}
          acceptedRecipients={acceptedRecipients}
          patientName={patientName}
        />
      )}
      <CloseReferralDialog
        open={isCloseDialogOpen}
        onClose={() => setIsCloseDialogOpen(false)}
        onConfirm={handleClose}
        isLoading={updateStatusMutation.isLoading}
        patientName={patientName}
      />
      <EditDischargeDateModal
        open={isEditDateDialogOpen}
        onClose={() => setIsEditDateDialogOpen(false)}
        onSave={handleEditDischargeDate}
        isLoading={updateReferralMutation.isLoading}
        currentDate={referral.target_discharge_date ?? null}
        patientName={patientName}
      />
    </>
  );

  return { actions, dialogElement };
}
